// Shared "what kind of media is this" helper for the client splash screen.
// Used both in the Super Admin upload/preview UI and the end-user splash
// overlay so the two stay in sync on what counts as a video vs a gif/image.

export const SPLASH_VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
  "video/ogg",
];

export const SPLASH_VIDEO_EXTENSIONS = [".mp4", ".webm", ".mov", ".ogg", ".ogv"];

export const SPLASH_ACCEPT_ATTR = [
  "image/gif",
  ...SPLASH_VIDEO_MIME_TYPES,
].join(",");

const extensionOf = (value) => {
  const clean = String(value).split("?")[0].split("#")[0].toLowerCase();
  const dot = clean.lastIndexOf(".");
  return dot >= 0 ? clean.slice(dot) : "";
};

// Returns 'video' | 'gif' | 'image'. Accepts a File (checked via its MIME
// type) or a URL/path string (checked via its extension, query-string safe —
// signed GCS URLs carry a real extension in the path followed by ?X-Goog-...).
export const getSplashMediaKind = (source) => {
  if (!source) return "image";

  if (typeof File !== "undefined" && source instanceof File) {
    if (SPLASH_VIDEO_MIME_TYPES.includes(source.type)) return "video";
    if (source.type === "image/gif") return "gif";
    const ext = extensionOf(source.name || "");
    if (SPLASH_VIDEO_EXTENSIONS.includes(ext)) return "video";
    if (ext === ".gif") return "gif";
    return "image";
  }

  const ext = extensionOf(source);
  if (SPLASH_VIDEO_EXTENSIONS.includes(ext)) return "video";
  if (ext === ".gif") return "gif";
  return "image";
};

export const isSplashFileAllowed = (file) => {
  if (!file) return false;
  if (file.type === "image/gif" || SPLASH_VIDEO_MIME_TYPES.includes(file.type)) {
    return true;
  }
  // Some browsers (older Safari especially) leave `type` blank for less common
  // video containers — fall back to the extension so those aren't rejected.
  const ext = extensionOf(file.name || "");
  return ext === ".gif" || SPLASH_VIDEO_EXTENSIONS.includes(ext);
};

// Total length of ONE pass of an animated GIF, in ms, by summing the frame
// delays in its Graphic Control Extensions. Browsers loop GIFs forever, so the
// splash uses this to stay up for exactly one play. Resolves null when the
// bytes can't be fetched (e.g. CORS) or parsed — callers fall back to a fixed
// duration.
export const getGifDurationMs = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const bytes = new Uint8Array(await res.arrayBuffer());
    if (String.fromCharCode(...bytes.subarray(0, 3)) !== "GIF") return null;

    const skipSubBlocks = (pos) => {
      while (pos < bytes.length && bytes[pos] !== 0) pos += bytes[pos] + 1;
      return pos + 1;
    };

    let pos = 13; // header (6) + logical screen descriptor (7)
    const screenFlags = bytes[10];
    if (screenFlags & 0x80) pos += 3 * (1 << ((screenFlags & 0x07) + 1));

    let total = 0;
    while (pos < bytes.length) {
      const block = bytes[pos];
      if (block === 0x3b) break; // trailer
      if (block === 0x21) {
        // Extension; 0xF9 = Graphic Control Extension carrying the frame delay
        if (bytes[pos + 1] === 0xf9) {
          const delayCs = bytes[pos + 4] | (bytes[pos + 5] << 8);
          // Browsers bump 0–1cs delays up to 10cs (100ms)
          total += (delayCs <= 1 ? 10 : delayCs) * 10;
        }
        pos = skipSubBlocks(pos + 2);
      } else if (block === 0x2c) {
        // Image descriptor (10) + optional local color table + LZW data
        const imgFlags = bytes[pos + 9];
        pos += 10;
        if (imgFlags & 0x80) pos += 3 * (1 << ((imgFlags & 0x07) + 1));
        pos = skipSubBlocks(pos + 1);
      } else {
        break; // unknown block — stop rather than misparse
      }
    }
    return total > 0 ? total : null;
  } catch {
    return null;
  }
};
